import React, { useEffect, useRef, useState } from 'react'
import { apiDelete, apiGet, apiPost } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'
import { IconButton } from '@mui/material'
import { Edit, Build } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useLocation } from 'react-router'
import AlertUI from '../../generic/AlertUI'
import SkillCapabilitiesDialog from './SkillCapabilitiesDialog'

const Workers = () => {
  const { t_ } = useSmartTranslation()
  const navigate = useNavigate()
  const { state } = useLocation()
  const [AlertElement, showAlert] = AlertUI()
  const show = useRef(showAlert)

  // D11 (2026-07-15, PO amendment): second entry point to the Skills modal, from this row's ⋮
  // menu (actions.custom, rendered by generic/ActionsMenu.jsx alongside "Edit").
  const [skillsDialogOpen, setSkillsDialogOpen] = useState(false)
  const [skillsWorkerId, setSkillsWorkerId] = useState('')

  useEffect(() => {
    if (state === 'BACK') {
      show.current({
        title: t_('There have been no modifications.'),
        severity: 'info'

      })
    } else if (state === 'EDIT') {
      show.current({
        title: t_('Worker updated correctly.'),
        severity: 'success'
      })
    }
  }, [state, t_])

  const columns = [{
    name: t_('Code'),
    field: 'code',
    charLimit: 70,
    mandatory: true,
    unique: true
  },
  {
    name: t_('Name'),
    field: 'name',
    charLimit: 70,
    mandatory: true
  },
  {
    name: t_('Last name'),
    field: 'surname',
    charLimit: 70,
    mandatory: true
  },
  {
    name: t_('Phone'),
    field: 'tlf',
    charLimit: 70
  },
  {
    name: t_('Email'),
    field: 'email',
    charLimit: 70
  },
  {
    name: t_('Calendar'),
    field: 'calendar.name',
    value: 'calendar.id',
    mandatory: true,
    type: 'select',
    // Sun sends the worker's calendar without its code: a seeded calendar reads by its name (P17).
    render: (row) => systemValueLabel(t_, 'calendar_reference', { name: row.calendar?.name || '' }),
    renderOnlyInReadMode: true,
    edit: {
      source: async () => {
        const calendarOptions = await apiGet('allCalendarReferences')
        return calendarOptions
      },
      id: 'id',
      field: 'name'
    }
  },
  {
    name: t_('Skills'),
    field: 'workerTechnologies.name',
    type: 'smartMultiSelect',
    // Not sortable: 'technology' is a to-many association; ORDER BY on it under SELECT DISTINCT
    // throws on PostgreSQL, and sorting by a multi-valued column is ill-defined (#2743 / story #53 D5).
    // `searchable: true` is the opt-in that keeps this column's search box enabled once it is no
    // longer sortable (the legacy rule disables search on non-sortable columns unless searchable).
    sortable: false,
    searchable: true,
    edit: {
      source: async () => {
        const technologyOptions = await apiGet('technologiesAndGroupsBasicData')
        return technologyOptions
      },
      id: 'fakeId',
      field: 'name'
    },
    mandatory: true
  },
  {
    name: t_('MOS capabilities'),
    field: 'mosCapabilities.name',
    type: 'smartMultiSelect',
    edit: {
      source: async () => {
        const mosCapabilityOptions = await apiGet('mos-capabilities')
        return mosCapabilityOptions
      },
      id: 'id',
      field: 'name'
    }
  }
  ]

  const openSkillsDialog = (row) => {
    setSkillsWorkerId(row.id)
    setSkillsDialogOpen(true)
  }

  const actions = {
    custom: [{
      name: 'Edit',
      render: (row) => {
        return (
          <IconButton>
            <Edit />
          </IconButton>
        )
      },
      onClick: (row) => navigate('/configuration/workers/' + row.id, { replace: true })
    }, {
      name: 'Skills',
      render: (row) => {
        return (
          <IconButton>
            <Build />
          </IconButton>
        )
      },
      onClick: (row) => openSkillsDialog(row)
    }],
    create: {
      fetchFunction: (row) => {
        return apiPost('worker', row)
      }
    },
    delete: {
      fetchFunction: (rows) => {
        if (rows.length === 1) {
          return apiDelete('worker/' + rows[0].id, rows)
        } else {
          return apiDelete('workerMulti', rows)
        }
      }
    }
  }

  const fetchFunction = async (pagination) => {
    const workers = await apiGet('worker', pagination)
    return workers
  }
  return (
    <>{AlertElement}
      <SmartMateriaUITable
        tableTuneUniqueKey='worker'
        columns={columns}
        title={t_('Workers')}
        dataFetch={fetchFunction}
        actions={actions}
        multipleSelection
        multipleOptions
        pageable
      />
      <SkillCapabilitiesDialog
        open={skillsDialogOpen}
        workerId={skillsWorkerId}
        onClose={() => setSkillsDialogOpen(false)}
        // No explicit refresh needed: SmartMateriaUITable polls its dataFetch every second, so
        // the "Skills" column catches up with the modal's changes on its own.
      />
    </>
  )
}

export default Workers
